import React, { useState, useEffect, useCallback } from "react";
import "../../Styles/NotesFollowUpTable.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { notesFollowupsAPI } from "../../api/index"; // adjust path to match your project
import { MANDALS } from "./NotesFollowUpForm"; // reuse the same id->name list — keeps them in sync

// ---------- Excel template settings ----------
// Headings follow the Add Note form fields (same order as the form).
// CONFIRM these with your backend if you add a bulk upload for notes.
const TEMPLATE_HEADERS = [
  "Subject",
  "Related Person",
  "Voter ID",
  "Mobile Number",
  "Category",
  "Priority",
  "Due Date",
  "Status",
  "Village",
  "Mandal",
  "Notes / Description",
];

const TEMPLATE_COLUMN_WIDTHS = [
  { wch: 45 }, // Subject
  { wch: 26 }, // Related Person
  { wch: 16 }, // Voter ID
  { wch: 16 }, // Mobile Number
  { wch: 24 }, // Category
  { wch: 12 }, // Priority
  { wch: 14 }, // Due Date
  { wch: 14 }, // Status
  { wch: 22 }, // Village
  { wch: 20 }, // Mandal
  { wch: 55 }, // Notes / Description
];

const TEMPLATE_INSTRUCTIONS = [
  ["Column", "Required", "Allowed values / Rules"],
  ["Subject", "Yes", "5-200 characters. Letters, numbers, spaces and . , - & ' ( ) / : ; ? ! only"],
  ["Related Person", "Yes", "3-100 characters. Alphabets, spaces, '.' and ''' only"],
  ["Voter ID", "No", "Optional. If filled: 3 capital letters + 7 digits (e.g. ABC1234567)"],
  ["Mobile Number", "No", "Optional. If filled: exactly 10 digits, must start with 6, 7, 8 or 9. Format the cell as Text in Excel"],
  ["Category", "Yes", "Grievance Follow-up, Scheme Follow-up, Development Work, General"],
  ["Priority", "Yes", "High, Medium, Low"],
  ["Due Date", "Yes", "Format: YYYY-MM-DD (e.g. 2026-10-03)"],
  ["Status", "Yes", "Open, In Progress, Completed"],
  ["Village", "No", "Optional. Alphabets and spaces only (2-100 characters)"],
  ["Mandal", "No", "Optional. " + MANDALS.map((m) => m.name).join(", ")],
  ["Notes / Description", "Yes", "10-1000 characters"],
];

const mandalName = (id) => MANDALS.find((m) => m.id === id)?.name || "—";

// backend <-> UI label maps
const STATUS_TO_LABEL = { open: "Open", in_progress: "In Progress", closed: "Completed" };
const STATUS_TO_BACKEND = { Open: "open", "In Progress": "in_progress", Completed: "closed" };
const PRIORITY_TO_LABEL = { high: "High", medium: "Medium", low: "Low" };
const PRIORITY_TO_BACKEND = { High: "high", Medium: "medium", Low: "low" };

const CATEGORY_ICONS = {
  "Grievance Follow-up": { icon: "💧", bg: "note123-icon-red" },
  "Scheme Follow-up": { icon: "🧑‍🤝‍🧑", bg: "note123-icon-orange" },
  "Development Work": { icon: "🛣️", bg: "note123-icon-green" },
  General: { icon: "📝", bg: "note123-icon-blue" },
};
const DEFAULT_ICON = { icon: "📝", bg: "note123-icon-blue" };

// Backend gives "YYYY-MM-DD"; UI originally showed "DD-MM-YYYY".
const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}-${month}-${year}`;
};

function capitalizeFallback(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
}

const PAGE_SIZE = 10;

const NotesFollowUpTable = () => {
  const navigate = useNavigate();

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [priorityFilter, setPriorityFilter] = useState("All Priorities");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [mandalFilter, setMandalFilter] = useState("All Mandals");
  const [currentPage, setCurrentPage] = useState(1);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ---------- FETCH ----------
  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await notesFollowupsAPI.getAll({});
      const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setNotes(data);
    } catch (err) {
      console.error("Failed to load notes:", err);
      setLoadError(err.response?.data?.detail || "Could not load notes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // ---------- DOWNLOAD EXCEL TEMPLATE (headings only) ----------
  const handleDownloadTemplate = () => {
    try {
      // Sheet 1: Template (headings only, ready to fill)
      const templateSheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
      templateSheet["!cols"] = TEMPLATE_COLUMN_WIDTHS;

      // Sheet 2: Instructions (allowed values and rules)
      const instructionSheet = XLSX.utils.aoa_to_sheet(TEMPLATE_INSTRUCTIONS);
      instructionSheet["!cols"] = [{ wch: 24 }, { wch: 10 }, { wch: 95 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, templateSheet, "Template");
      XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");

      XLSX.writeFile(workbook, "Notes_FollowUp_Template.xlsx");
      toast.success("Template downloaded successfully");
    } catch (err) {
      console.error("Template download failed:", err);
      toast.error("Could not download the template");
    }
  };

  const resetFilters = () => {
    setSearch("");
    setCategoryFilter("All Categories");
    setPriorityFilter("All Priorities");
    setStatusFilter("All Status");
    setMandalFilter("All Mandals");
    setCurrentPage(1);
  };

  // ---------- SEARCH + FILTER (client-side) ----------
  const filteredNotes = notes.filter((item) => {
    const searchLower = search.trim().toLowerCase();

    const matchesSearch =
      searchLower === "" ||
      (item.subject || "").toLowerCase().includes(searchLower) ||
      (item.related_person || "").toLowerCase().includes(searchLower);

    const matchesCategory =
      categoryFilter === "All Categories" || item.category === categoryFilter;

    const priorityLabel = PRIORITY_TO_LABEL[item.priority] || item.priority;
    const matchesPriority =
      priorityFilter === "All Priorities" || priorityLabel === priorityFilter;

    const statusLabel = STATUS_TO_LABEL[item.status] || item.status;
    const matchesStatus = statusFilter === "All Status" || statusLabel === statusFilter;

    const matchesMandal =
      mandalFilter === "All Mandals" || String(item.mandal_id) === mandalFilter;

    return matchesSearch && matchesCategory && matchesPriority && matchesStatus && matchesMandal;
  });

  const totalPages = Math.max(1, Math.ceil(filteredNotes.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const pageNotes = filteredNotes.slice(startIndex, startIndex + PAGE_SIZE);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // ---------- STATS ----------
  const totalNotes = notes.length;
  const highPriority = notes.filter((n) => n.priority === "high").length;

  const dueThisWeek = notes.filter((n) => {
    if (!n.due_date) return false;
    const due = new Date(n.due_date + "T00:00:00");
    const today = new Date();
    const weekFromNow = new Date();
    weekFromNow.setDate(today.getDate() + 7);
    return due >= today && due <= weekFromNow;
  }).length;

  const completedCount = notes.filter((n) => n.status === "closed").length;

  const getPriorityClass = (priorityLabel) => {
    switch (priorityLabel) {
      case "High":
        return "note123-priority-high";
      case "Medium":
        return "note123-priority-medium";
      case "Low":
        return "note123-priority-low";
      default:
        return "";
    }
  };

  const getStatusClass = (statusLabel) => {
    switch (statusLabel) {
      case "Open":
        return "note123-status-open";
      case "In Progress":
        return "note123-status-progress";
      case "Completed":
        return "note123-status-completed";
      default:
        return "";
    }
  };

  const getRowAccent = (priorityLabel) => {
    switch (priorityLabel) {
      case "High":
        return "note123-accent-red";
      case "Medium":
        return "note123-accent-orange";
      case "Low":
        return "note123-accent-green";
      default:
        return "";
    }
  };

  // ---------- EDIT LOGIC ----------
  const openEditModal = (item) => {
    setEditItem({
      id: item.id,
      subject: item.subject || "",
      person: item.related_person || "",
      voterId: item.epic_no || "",
      mobile: item.mobile_number || "",
      category: item.category || "",
      priority: PRIORITY_TO_LABEL[item.priority] || "",
      dueDate: item.due_date || "",
      village: "", // backend doesn't return village name, only village_id
      mandalId: item.mandal_id ? String(item.mandal_id) : "", // pre-fill from the id we DO have
      status: STATUS_TO_LABEL[item.status] || "Open",
      description: item.notes_description || "",
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const resolvedMandalName = editItem.mandalId
        ? MANDALS.find((m) => String(m.id) === String(editItem.mandalId))?.name
        : undefined;

      await notesFollowupsAPI.update(editItem.id, {
        subject: editItem.subject,
        related_person: editItem.person,
        epic_no: editItem.voterId || null,
        mobile_number: editItem.mobile || null,
        category: editItem.category,
        priority: PRIORITY_TO_BACKEND[editItem.priority] ?? null,
        due_date: editItem.dueDate,
        status: STATUS_TO_BACKEND[editItem.status] ?? "open",
        village_name: editItem.village || undefined,
        mandal_name: resolvedMandalName,
        notes_description: editItem.description,
      });

      await fetchNotes();
      closeEditModal();
    } catch (err) {
      console.error("Failed to update note:", err);
      alert(err.response?.data?.detail || "Something went wrong while saving changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  // ---------- DELETE LOGIC ----------
  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await notesFollowupsAPI.delete(deleteId);
      await fetchNotes();
      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete note:", err);
      alert(err.response?.data?.detail || "Could not delete this note.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="note123-page">
      {/* Header */}
      <div className="note123-header">
        <div>
          <h2 className="note123-title">Notes &amp; Follow Ups</h2>
          <p className="note123-subtitle">
            Track important follow-ups, reminders and notes for better coordination.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="note123-add-btn"
            onClick={handleDownloadTemplate}
            style={{ background: "#16a34a" }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>{" "}
            Download Template
          </button>

          <button
            className="note123-add-btn"
            onClick={() => navigate("/dashboard/NotesFollowUpForm")}
          >
            <span className="note123-add-icon">+</span> Add New Note
          </button>
        </div>
      </div>

      {loadError && <div className="note123-no-data">{loadError}</div>}

      {/* Stat Cards */}
      <div className="note123-stats-grid">
        <div className="note123-stat-card note123-stat-card-blue">
          <div className="note123-stat-icon note123-stat-icon-blue">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
              <path d="M14 2v6h6M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="note123-stat-info">
            <span className="note123-stat-label">Total Notes</span>
            <span className="note123-stat-value">{totalNotes}</span>
          </div>
        </div>

        <div className="note123-stat-card note123-stat-card-red">
          <div className="note123-stat-icon note123-stat-icon-red">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6 3v18M6 4h11l-2.5 3L17 10H6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="note123-stat-info">
            <span className="note123-stat-label">High Priority</span>
            <span className="note123-stat-value">{highPriority}</span>
          </div>
        </div>

        <div className="note123-stat-card note123-stat-card-orange">
          <div className="note123-stat-icon note123-stat-icon-orange">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
              <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="note123-stat-info">
            <span className="note123-stat-label">Due This Week</span>
            <span className="note123-stat-value">{dueThisWeek}</span>
          </div>
        </div>

        <div className="note123-stat-card note123-stat-card-green">
          <div className="note123-stat-icon note123-stat-icon-green">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
              <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="note123-stat-info">
            <span className="note123-stat-label">Completed</span>
            <span className="note123-stat-value">{completedCount}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="note123-filters">
        <div className="note123-search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by Subject or Person..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <select
          className="note123-select"
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Categories</option>
          <option>Grievance Follow-up</option>
          <option>Scheme Follow-up</option>
          <option>Development Work</option>
          <option>General</option>
        </select>

        <select
          className="note123-select"
          value={priorityFilter}
          onChange={(e) => {
            setPriorityFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Priorities</option>
          <option>High</option>
          <option>Medium</option>
          <option>Low</option>
        </select>

        <select
          className="note123-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Status</option>
          <option>Open</option>
          <option>In Progress</option>
          <option>Completed</option>
        </select>

        <select
          className="note123-select"
          value={mandalFilter}
          onChange={(e) => {
            setMandalFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="All Mandals">All Mandals</option>
          {MANDALS.map((m) => (
            <option key={m.id} value={String(m.id)}>
              {m.name}
            </option>
          ))}
        </select>

        <button className="note123-reset-btn" onClick={resetFilters}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 4v6h6M20 20v-6h-6M4.5 15a8 8 0 0014.5 3.5M19.5 9A8 8 0 005 5.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Clear Filters
        </button>
      </div>

      {/* Notes List */}
      <div className="note123-list">
        {loading ? (
          <div className="note123-no-data">Loading...</div>
        ) : pageNotes.length === 0 ? (
          <div className="note123-no-data">No notes found.</div>
        ) : (
          pageNotes.map((item) => {
            const priorityLabel = PRIORITY_TO_LABEL[item.priority] || "—";
            const statusLabel = STATUS_TO_LABEL[item.status] || capitalizeFallback(item.status);
            const { icon, bg } = CATEGORY_ICONS[item.category] || DEFAULT_ICON;

            return (
              <div
                className={`note123-card ${getRowAccent(priorityLabel)}`}
                key={item.id}
              >
                <div className={`note123-card-icon ${bg}`}>
                  <span>{icon}</span>
                </div>

                <div className="note123-card-main">
                  <h3 className="note123-card-subject">{item.subject}</h3>

                  <div className="note123-card-meta">
                    <span className="note123-meta-item">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.7" />
                        <path d="M4 20c0-3.5 3.6-6 8-6s8 2.5 8 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                      </svg>
                      {item.related_person || "—"}
                    </span>

                    {item.mobile_number && (
                      <span className="note123-meta-item">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.7a2 2 0 01-.5 2.1L8 9.7a16 16 0 006 6l1.2-1.2a2 2 0 012.1-.5c.9.3 1.8.5 2.7.6a2 2 0 011.7 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                        </svg>
                        {item.mobile_number}
                      </span>
                    )}

                    <span className="note123-meta-item">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                      </svg>
                      {item.category || "—"}
                    </span>

                    <span className="note123-meta-item">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinejoin="round"
                        />
                        <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.7" />
                      </svg>
                      {/* Village name still unavailable (only village_id comes back);
                          mandal name is now resolved from mandal_id via MANDALS */}
                      {item.village_id != null ? `Village #${item.village_id}` : "—"}, {mandalName(item.mandal_id)}
                    </span>
                  </div>
                </div>

                <div className="note123-card-priority">
                  <span className={`note123-priority-badge ${getPriorityClass(priorityLabel)}`}>
                    {priorityLabel === "High" && "↑ "}
                    {priorityLabel === "Medium" && "— "}
                    {priorityLabel === "Low" && "↓ "}
                    {priorityLabel}
                  </span>

                  <span className="note123-due-date">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.7" />
                      <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    </svg>
                    {formatDisplayDate(item.due_date)}
                  </span>
                </div>

                <div className="note123-card-status">
                  <span className={`note123-status-badge ${getStatusClass(statusLabel)}`}>
                    <span className="note123-status-dot" />
                    {statusLabel}
                  </span>
                </div>

                <div className="note123-card-actions">
                  <button className="note123-edit-btn" onClick={() => openEditModal(item)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Edit
                  </button>

                  <button
                    className="note123-more-btn"
                    onClick={() => openDeleteModal(item.id)}
                    title="Delete"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="5" cy="12" r="1.8" />
                      <circle cx="12" cy="12" r="1.8" />
                      <circle cx="19" cy="12" r="1.8" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      <div className="note123-pagination-bar">
        <span className="note123-pagination-info">
          Showing {pageNotes.length === 0 ? 0 : startIndex + 1} to{" "}
          {startIndex + pageNotes.length} of {filteredNotes.length} entries
        </span>

        <div className="note123-pagination-controls">
          <button
            className="note123-page-arrow"
            onClick={() => handlePageChange(safePage - 1)}
          >
            &lt;
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`note123-page-num ${safePage === p ? "note123-active" : ""}`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ))}

          <button
            className="note123-page-arrow"
            onClick={() => handlePageChange(safePage + 1)}
          >
            &gt;
          </button>
        </div>
      </div>

      {/* ---------------- EDIT MODAL ---------------- */}
      {showEditModal && editItem && (
        <div className="note123-modal-overlay" onClick={closeEditModal}>
          <div className="note123-modal" onClick={(e) => e.stopPropagation()}>
            <div className="note123-modal-header">
              <h3>Edit Note</h3>
              <button className="note123-modal-close" onClick={closeEditModal}>
                &times;
              </button>
            </div>

            <form className="note123-modal-body" onSubmit={handleEditSave}>
              <div className="note123-form-group">
                <label>Subject</label>
                <input
                  type="text"
                  name="subject"
                  value={editItem.subject}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="note123-form-row">
                <div className="note123-form-group">
                  <label>Person</label>
                  <input
                    type="text"
                    name="person"
                    value={editItem.person}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                <div className="note123-form-group">
                  <label>Mobile</label>
                  <input
                    type="text"
                    name="mobile"
                    value={editItem.mobile}
                    onChange={handleEditChange}
                  />
                </div>
              </div>

              <div className="note123-form-row">
                <div className="note123-form-group">
                  <label>Voter ID</label>
                  <input
                    type="text"
                    name="voterId"
                    value={editItem.voterId}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="note123-form-group">
                  <label>Category</label>
                  <select name="category" value={editItem.category} onChange={handleEditChange}>
                    <option>Grievance Follow-up</option>
                    <option>Scheme Follow-up</option>
                    <option>Development Work</option>
                    <option>General</option>
                  </select>
                </div>
              </div>

              <div className="note123-form-row">
                <div className="note123-form-group">
                  <label>Priority</label>
                  <select name="priority" value={editItem.priority} onChange={handleEditChange}>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>

                <div className="note123-form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    name="dueDate"
                    value={editItem.dueDate}
                    onChange={handleEditChange}
                    required
                  />
                </div>
              </div>

              <div className="note123-form-row">
                <div className="note123-form-group">
                  <label>Village (leave blank to keep current)</label>
                  <input
                    type="text"
                    name="village"
                    placeholder="Only fill in to change"
                    value={editItem.village}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="note123-form-group">
                  <label>Mandal</label>
                  <select name="mandalId" value={editItem.mandalId} onChange={handleEditChange}>
                    <option value="">Choose Mandal</option>
                    {MANDALS.map((m) => (
                      <option key={m.id} value={String(m.id)}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="note123-form-group">
                <label>Status</label>
                <select name="status" value={editItem.status} onChange={handleEditChange}>
                  <option>Open</option>
                  <option>In Progress</option>
                  <option>Completed</option>
                </select>
              </div>

              <div className="note123-form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  rows="3"
                  value={editItem.description}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="note123-modal-footer">
                <button
                  type="button"
                  className="note123-btn-cancel"
                  onClick={closeEditModal}
                  disabled={savingEdit}
                >
                  Cancel
                </button>
                <button type="submit" className="note123-btn-save" disabled={savingEdit}>
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- DELETE CONFIRM MODAL ---------------- */}
      {showDeleteModal && (
        <div className="note123-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="note123-modal note123-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="note123-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h3 className="note123-confirm-title">Delete this note?</h3>
            <p className="note123-confirm-text">
              This action cannot be undone. Are you sure you want to delete
              this note?
            </p>

            <div className="note123-confirm-footer">
              <button className="note123-btn-cancel" onClick={closeDeleteModal} disabled={deleting}>
                Cancel
              </button>
              <button className="note123-btn-delete" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "OK, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesFollowUpTable;